// ============================================================
// useWaitingRoomActions - everything you can do to a waiting room.
//
// Eight verbs that all read the same room list and write it back the
// same way, extracted from the composition layer where they had grown
// to ~230 lines beside the combat log, the map and the dice tray
// (v8.48.0).
//
// `useWaitingRooms` stays outside this hook and is passed in, because
// the admission door needs the room list to take its census and this
// needs the admission door to stage anybody - one of the two has to be
// created first, and it is the sync.
//
// EVERY WRITE IS ONE WRITE. A room is a document; building the next one
// from a state a previous call in the same tick has already moved past
// is how a sweep of six put one person in the room (v8.24.0).
// ============================================================
import { useCallback, useMemo, useState } from 'react';
import { makeRoom, addToRoom, takeFrom, takeNamed, removeFromRoom, moveBetween } from '../engine/roster/waitingRoom';
import { combatantFromCharacter } from '../engine/roster/factories';
import { planReplace, inheritTeam, refuseReplace } from '../engine/roster/replace';

const newRoomId = () => `room-${Date.now().toString(36)}`;
const roomById = (rooms, id) => rooms.find((r) => r.id === id);
const roomHolding = (rooms, combatantId) => rooms.find((r) => r.entries.some((c) => c.id === combatantId));

/**
 * @param {Object} params
 * @param {Object}   params.waiting       `useWaitingRooms`: rooms, writeRoom, removeRoom
 * @param {Array}    params.roster
 * @param {Function} params.setRoster
 * @param {Function} params.removeToken
 * @param {Function} params.removeCombatant
 * @param {Function} params.pointAtIfUnset
 * @param {Function} params.narrate
 * @param {Function} params.admit          the admission door: census, naming, library
 * @param {Function} params.record
 * @param {Function} [params.admitToTurnOrder]  Put deployed combatants into the turn order. Omitted = tracker inactive.
 * @param {Function} [params.removeFromTurnOrder]  Take somebody out of the turn order. Null when the
 *   `recallKeepsTurn` house rule says a room is a wing rather than an exit - but DISPOSAL always
 *   calls `forceRemoveFromTurnOrder`, because dismissing somebody from a room is the end of them.
 * @param {Function} [params.forceRemoveFromTurnOrder]  Same, ignoring the house rule. For disposal.
 * @param {Object}   params.tokens         For a replace (v8.343.0): the live tokens by combatant id
 * @param {Function} params.writeToken     Write the newcomer's token BEFORE the roster learns of them
 * @param {Function} params.isDefencePending  Is this combatant answering an attack? A replace refuses while they are.
 * @param {Function} [params.replaceInTurnOrder]  `(outgoingId, incomingId, name)` - the newcomer takes the
 *   departed's row. Omitted = tracker inactive.
 * @param {Function} [params.repoint]      `(fromId, toId)` - move the target and actor pointers off the departed
 */
export default function useWaitingRoomActions(params) {
  const { waiting } = params;
  const [activeRoomId, setActiveRoomId] = useState(null);
  const activeRoom = roomById(waiting.rooms, activeRoomId) ?? waiting.rooms[0] ?? null;
  const ctx = { ...params, activeRoom, setActiveRoomId };
  const { newRoom, dismissFromRoom, moveToRoom, deleteRoom } = useRoomHousekeeping(ctx);
  const sendToRoom = useSendToRoom(ctx);
  const { deployRoom, deployMember } = useDeploy(ctx);
  const { recallToRoom, recallManyToRoom } = useRecall(ctx);
  const replaceWith = useReplace(ctx);
  return {
    rooms: waiting.rooms,
    activeRoom,
    activeRoomId,
    setActiveRoomId,
    newRoom,
    sendToRoom,
    dismissFromRoom,
    moveToRoom,
    deleteRoom,
    deployRoom,
    deployMember,
    recallToRoom,
    recallManyToRoom,
    replaceWith,
  };
}

/** Make, empty, move between and delete rooms. */
function useRoomHousekeeping({ waiting, setActiveRoomId, forceRemoveFromTurnOrder }) {
  const newRoom = useCallback(() => {
    const name = window.prompt('Name this waiting room', `Room ${waiting.rooms.length + 1}`);
    if (!name) return;
    const room = makeRoom(newRoomId(), name.trim(), waiting.rooms.length);
    waiting.writeRoom(room);
    setActiveRoomId(room.id);
  }, [waiting, setActiveRoomId]);

  const dismissFromRoom = useDismissFromRoom(waiting, forceRemoveFromTurnOrder);
  const moveToRoom = useMoveToRoom(waiting);
  const deleteRoom = useDeleteRoom(waiting, setActiveRoomId);
  return { newRoom, dismissFromRoom, moveToRoom, deleteRoom };
}

/**
 * Take somebody out of a room and drop them.
 *
 * NOTHING IS REALLY LOST, which is why it does not ask. A staged combatant
 * was never on the board, and the character file it was built from is
 * untouched on disk - re-stage them whenever you like.
 */
function useDismissFromRoom(waiting, forceRemoveFromTurnOrder) {
  return useCallback(
    (combatantId) => {
      const room = roomHolding(waiting.rooms, combatantId);
      if (!room) return;
      waiting.writeRoom(removeFromRoom(room, [combatantId]));
      // Gone for good, so the order loses them whatever the house rule
      // says about recalls - there is nobody left to come back.
      forceRemoveFromTurnOrder?.(combatantId);
    },
    [waiting, forceRemoveFromTurnOrder],
  );
}

/**
 * Move somebody from whichever room holds them into another.
 *
 * TWO WRITES, because they are two documents - but built from ONE reading
 * of the rooms, so the pair cannot disagree about who was where.
 * `moveBetween` returns the same array when nothing moved, which is how
 * "dropped on the room they were already in" costs nothing (v8.25.0).
 */
function useMoveToRoom(waiting) {
  return useCallback(
    (combatantId, toRoomId) => {
      const next = moveBetween(waiting.rooms, combatantId, toRoomId);
      if (next === waiting.rooms) return;
      for (const room of next) {
        if (roomById(waiting.rooms, room.id) !== room) waiting.writeRoom(room);
      }
    },
    [waiting],
  );
}

/**
 * Delete a room.
 *
 * ASKS ONLY WHEN SOMEBODY IS INSIDE. An empty room is a name, and a name
 * you can retype is not worth a dialogue.
 */
function useDeleteRoom(waiting, setActiveRoomId) {
  return useCallback(
    (roomId) => {
      const room = roomById(waiting.rooms, roomId);
      if (!room) return;
      if (room.entries.length && !window.confirm(`${room.name} has ${room.entries.length} waiting. Delete it?`)) return;
      waiting.removeRoom(roomId);
      const other = waiting.rooms.find((r) => r.id !== roomId)?.id ?? null;
      setActiveRoomId((prev) => (prev === roomId ? other : prev));
    },
    [waiting, setActiveRoomId],
  );
}

/**
 * The room staging goes into: the one asked for, else the active room -
 * or a NEW one, when asked for (`new:...`) or when there are no rooms at
 * all. Null when the name prompt was cancelled.
 *
 * A NEW ROOM IF ASKED FOR. Staging into a room that does not exist yet
 * used to return silently, so the button looked broken - naming one here
 * means the Character menu can make its own (v8.26.0). NO ROOMS AT ALL
 * MEANS MAKE ONE, rather than returning silently: "Active room" is the
 * default choice, and in a fresh session there is no active room - so the
 * commonest way to reach this was also the one way it did nothing
 * (v8.34.0).
 *
 * ASK FOR THE NAME, as `+ Room` does. It named them `Room 1`, `Room 2`
 * silently - which is a room you did not choose the name of, in a rail
 * whose whole job is telling rooms apart (v8.30.0). Cancelling means
 * cancelling, not "make one anyway".
 */
function stagingRoom(rooms, roomId, activeRoom) {
  const target = roomById(rooms, roomId) ?? activeRoom;
  if (!(roomId ?? '').startsWith('new:') && target) return { room: target, made: false };
  const suggested = `Room ${rooms.length + 1}`;
  const name = window.prompt('Name this waiting room', suggested);
  if (name === null) return null;
  return { room: makeRoom(newRoomId(), name.trim() || suggested, rooms.length), made: true };
}

/**
 * Stage characters rather than loading them.
 *
 * They become COMBATANTS here - a room holds the whole thing, so somebody
 * staged and somebody recalled are the same shape - but they do not touch
 * the roster, which is what keeps them hidden.
 *
 * Returns the room's name, or `null` when nothing happened. WHICH ROOM IT
 * ACTUALLY USED: only this knows - it may have just made one - so the
 * menu is told rather than left to guess from a chooser that still reads
 * "Active room" (v8.38.0).
 */
function useSendToRoom({ waiting, activeRoom, admit, record, setActiveRoomId }) {
  return useCallback(
    (characters, roomId) => {
      const picked = stagingRoom(waiting.rooms, roomId, activeRoom);
      if (!picked) return null;
      const target = picked.room;
      if (picked.made) setActiveRoomId(target.id);
      // THE CENSUS IS TAKEN BEFORE THEY ARE ADDED - it counts the rooms as
      // well as the roster, so a name minted here cannot collide with the
      // table, and taking it afterwards would count the arrivals as though
      // they were already standing there.
      const { arrivals, distinct } = admit(characters);
      const parked = arrivals.map((a, i) =>
        combatantFromCharacter(a.character, { order: target.entries.length + i, name: a.name }),
      );
      waiting.writeRoom(addToRoom(target, parked));
      record(distinct);
      return target.name;
    },
    [waiting, activeRoom, admit, record, setActiveRoomId],
  );
}

/**
 * Bring people from a room onto the board.
 *
 * They join the roster and `useAutoPlacedTokens` rings them around the
 * marker one render later - the same arrival path a directly loaded
 * character takes, because from the map's point of view they are the same
 * event. THE ROOM IS EMPTIED, NOT DELETED: it is a place you named, and the
 * next wave goes in the same one.
 */
function useDeploy({ waiting, narrate, ...ctx }) {
  const joinTheFight = useJoinTheFight(ctx);
  const deployRoom = useCallback(
    (roomId, count) => {
      const room = roomById(waiting.rooms, roomId);
      if (!room || room.entries.length === 0) return;
      const { taken, rest } = takeFrom(room, count ?? room.entries.length);
      if (!taken.length) return;
      joinTheFight(taken);
      waiting.writeRoom(rest);
      narrate(`🚪 ${taken.length} from ${room.name} joined the fight.`);
    },
    [waiting, joinTheFight, narrate],
  );
  const deployMember = useDeployMember(waiting, joinTheFight, narrate);
  return { deployRoom, deployMember };
}

/**
 * One named person out of whichever room is holding them.
 *
 * BY NAME, NOT FROM THE FRONT - the gesture is a drag of one specific body
 * onto one specific spot, so the queue order it happens to sit at is not
 * the question being asked. Where they land is decided by the map, which
 * has already noted the spot.
 */
function useDeployMember(waiting, joinTheFight, narrate) {
  return useCallback(
    (combatantId) => {
      const room = roomHolding(waiting.rooms, combatantId);
      if (!room) return;
      const { taken, rest } = takeNamed(room, [combatantId]);
      if (!taken.length) return;
      joinTheFight(taken);
      waiting.writeRoom(rest);
      narrate(`🚪 ${taken[0].name} joined the fight from ${room.name}.`);
    },
    [waiting, joinTheFight, narrate],
  );
}

/** Put arrivals on the roster in order, and point at the first if nothing is pointed at. */
function useJoinTheFight({ setRoster, pointAtIfUnset, admitToTurnOrder }) {
  return useCallback(
    (taken) => {
      setRoster((prev) => [...prev, ...taken.map((c, i) => ({ ...c, order: prev.length + i }))]);
      pointAtIfUnset(taken[0].id);
      // AND INTO THE TURN ORDER (v8.72.0). Deploying a room put people on
      // the roster and on the map but never in the tracker, so
      // reinforcements arrived with no turn at all - they were simply
      // absent from the order until somebody right-clicked each tab. The
      // combatants are in hand, so their sheets are too - no lookup
      // against a roster that has only just been told about them.
      admitToTurnOrder?.(taken.map((c) => ({ combatantId: c.id, name: c.name, sheet: c.sheet })));
    },
    [setRoster, pointAtIfUnset, admitToTurnOrder],
  );
}

const fellBackLine = (going, room) =>
  going.length === 1
    ? `🚪 ${going[0].name} fell back to ${room.name}.`
    : `🚪 ${going.length} fell back to ${room.name}.`;

/**
 * A HIDDEN FIGURE NEVER GOES TO A ROOM (plan map-display, G1): a room is
 * readable by a granted player, so it would be shown. The GM is told to
 * reveal it first; everyone else goes as asked.
 */
function keepsHidden(ids, roster, isHidden, refuse) {
  if (!isHidden) return ids;
  const held = ids.filter((id) => isHidden(id));
  if (held.length) {
    const names = roster.filter((c) => held.includes(c.id)).map((c) => c.name);
    refuse?.(
      `${names.join(', ')} ${names.length > 1 ? 'are' : 'is'} hidden: reveal them before sending them to a room.`,
    );
  }
  return ids.filter((id) => !isHidden(id));
}

/**
 * Take people off the map and put them back in the active room.
 *
 * THE WHOLE COMBATANT GOES, wounds, conditions, ammunition and log intact -
 * a room holds combatants, so this is a move rather than a conversion. A
 * squad can fall back, wait, and come on again somewhere else still
 * bleeding. They leave the roster, which is what makes them invisible
 * again.
 *
 * ONE WRITE FOR THE WHOLE GROUP. Calling this once per body would build
 * each room document from a state the previous call had already moved
 * past, and the last write would win - which is how a sweep of six put one
 * person in the room (v8.24.0). One person goes through the same door, so
 * the two cannot drift apart.
 */
function useRecall({ activeRoom, roster, setRoster, waiting, narrate, isHidden, refuse, ...leave }) {
  const offTheBoard = useOffTheBoard(leave);
  const recallManyToRoom = useCallback(
    (combatantIds) => {
      const room = activeRoom;
      if (!room || !combatantIds?.length) return;
      const wanted = new Set(keepsHidden(combatantIds, roster, isHidden, refuse));
      const going = roster.filter((c) => wanted.has(c.id));
      if (!going.length) return;
      setRoster((prev) => prev.filter((c) => !wanted.has(c.id)));
      going.forEach(offTheBoard);
      waiting.writeRoom(addToRoom(room, going));
      narrate(fellBackLine(going, room));
    },
    [activeRoom, roster, setRoster, offTheBoard, waiting, narrate, isHidden, refuse],
  );
  const recallToRoom = useCallback((combatantId) => recallManyToRoom([combatantId]), [recallManyToRoom]);
  return { recallToRoom, recallManyToRoom };
}

/**
 * Drop one recalled combatant's token, document and turn.
 *
 * AT ONCE, not an effect-tick later. The departure effect deletes the
 * shared document too, but a snapshot arriving in the gap re-adds whoever
 * it still lists - which put a recalled marker back on the map while its
 * copy sat in the room (v8.30.0). OFF THE BOARD, OUT OF THE ORDER
 * (v8.78.0): recall cleaned up the roster, the token and the shared
 * document but left the tracker chip behind, so the turn stopped on
 * somebody who was not there to take it. `removeFromTurnOrder` is null
 * when the GM has ruled otherwise.
 */
function useOffTheBoard({ removeToken, removeCombatant, removeFromTurnOrder }) {
  return useCallback(
    (c) => {
      removeToken(c.id);
      removeCombatant(c.id);
      removeFromTurnOrder?.(c.id);
    },
    [removeToken, removeCombatant, removeFromTurnOrder],
  );
}

const NOBODY = { incoming: null, room: null, fromRoom: null };

/** A stand-in from a room: the member leaves that room as the departed enters it. */
function standInFromRoom(rooms, { roomId, memberId }) {
  const fromRoom = roomById(rooms, roomId) ?? roomHolding(rooms, memberId);
  const incoming = fromRoom?.entries.find((c) => c.id === memberId) ?? null;
  return { incoming, room: fromRoom, fromRoom };
}

/**
 * A stand-in from the library, admitted through the same door
 * `sendToRoom` uses; the departed goes to the room asked for or the
 * active one.
 */
function standInFromLibrary({ roomId, character }, { rooms, activeRoom, admit, record, outgoing }) {
  const room = roomById(rooms, roomId) ?? activeRoom;
  const { arrivals, distinct } = admit([character]);
  const a = arrivals[0];
  const incoming = a ? combatantFromCharacter(a.character, { order: outgoing?.order ?? 0, name: a.name }) : null;
  if (incoming) record(distinct);
  return { incoming, room, fromRoom: null };
}

/** Who is coming, the room the departed goes to, and the room the newcomer leaves. */
function resolveStandIn(source, ctx) {
  if (source?.memberId) return standInFromRoom(ctx.rooms, source);
  if (source?.character) return standInFromLibrary(source, ctx);
  return NOBODY;
}

/**
 * Perform an unrefused swap, in THE ORDER OF WRITES the test asserts (see
 * useReplace). `fromRoom`, when set, IS `room`: a member's own room
 * receives the departed, so the newcomer leaves it in the same write.
 */
function performReplace({ outgoingId, outgoing, incoming, room, fromRoom, tokens }, act) {
  const newcomer = { ...inheritTeam(incoming, outgoing), order: outgoing.order };
  const plan = planReplace({ outgoing, incoming: newcomer, token: tokens[outgoingId] });
  act.writeToken(plan.token, { immediate: true });
  act.setRoster((prev) => prev.map((c) => (c.id === outgoingId ? newcomer : c)));
  act.removeToken(outgoingId);
  act.removeCombatant(outgoingId);
  act.replaceInTurnOrder?.(outgoingId, newcomer.id, newcomer.name);
  const withoutNewcomer = fromRoom ? removeFromRoom(room, [newcomer.id]) : room;
  act.waiting.writeRoom(addToRoom(withoutNewcomer, [outgoing]));
  act.repoint?.(outgoingId, newcomer.id);
  // Addressed to the NEWCOMER's log - the departed's is in the room now.
  act.narrate(`🔁 ${newcomer.name} stands in for ${outgoing.name}, who fell back to ${room.name}.`, newcomer.id);
}

/**
 * One person stands in for another, on the same spot (v8.343.0).
 *
 * A SWAP WITH THE WINGS: the newcomer takes the departed's feet and their
 * row in the order, the departed goes into a room with wounds, conditions
 * and ammunition intact - a recall and a deploy in one motion, minus the
 * walk from the marker. `source` is a room member (`{ roomId, memberId }`)
 * or a library character (`{ character, roomId? }`); with no room at all
 * the departed goes to a "Reserve" made on the spot.
 *
 * THE ORDER OF WRITES, which the test asserts:
 *   0. `refuseReplace` - nothing has moved.
 *   1. the newcomer's TOKEN, immediately - BEFORE the roster change, so
 *      `useAutoPlacedTokens` finds a token under their id and never rings
 *      them at the marker.
 *   2. the roster: the newcomer in the departed's place in the array.
 *   3. the departed's token and combatant documents, at once - the same
 *      reason a recall does it: a snapshot in the gap would re-add them.
 *   4. the turn order - same row, new identity.
 *   5. the room - ONE write: the newcomer out, the departed in.
 *   6. the pointers, so a panel aimed at the departed now shows the
 *      stand-in rather than whoever `useRoster` falls back to.
 *   7. one log line.
 *
 * Returns the refusal sentence, or `null` when the swap happened.
 */
function useReplace(ctx) {
  const { roster, waiting, activeRoom, admit, record, isDefencePending, tokens, setActiveRoomId } = ctx;
  const { writeToken, setRoster, removeToken, removeCombatant, replaceInTurnOrder, repoint, narrate } = ctx;
  const act = useMemo(
    () => ({ writeToken, setRoster, removeToken, removeCombatant, replaceInTurnOrder, repoint, narrate, waiting }),
    [writeToken, setRoster, removeToken, removeCombatant, replaceInTurnOrder, repoint, narrate, waiting],
  );
  return useCallback(
    (outgoingId, source) => {
      const outgoing = roster.find((c) => c.id === outgoingId);
      const who = resolveStandIn(source, { rooms: waiting.rooms, activeRoom, admit, record, outgoing });
      if (!who.incoming) return 'Nobody to stand in.';
      const pendingIds = new Set(roster.filter((c) => isDefencePending?.(c.id)).map((c) => c.id));
      const args = { pendingIds, outgoingId, incomingId: who.incoming.id, roster, tokens: tokens ?? {} };
      const refusal = refuseReplace(args);
      if (refusal) return refusal;
      // A room to receive the departed, made on the spot when there is
      // none - the swap must not fail for want of a shelf.
      const room = who.room ?? makeRoom(newRoomId(), 'Reserve', waiting.rooms.length);
      if (!who.room) setActiveRoomId(room.id);
      performReplace({ ...who, room, outgoingId, outgoing, tokens }, act);
      return null;
    },
    [roster, waiting, activeRoom, admit, record, isDefencePending, tokens, setActiveRoomId, act],
  );
}
